import {
  CreateNoteDto,
  ForceUpdateDeliveryDto,
  ResendNotificationDto,
  TimelineDto,
  KnownReallocateDeliveryDtoChannel,
  CanIOpenDeliveryContainerDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useApi } from '../../../core/api/api.context';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { RestError } from '@azure/core-http';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import { useTimelineUpdatesEventCentre } from '../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { useCallback, useMemo } from 'react';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionStream
} from '../../../events/timelineUpdates/timelineUpdates.types';
import { Delivery } from '../models/delivery.model';

export const useQueryGetDelivery = (deliveryID: string) => {
  const api = useApi();

  const query = useQuery<Delivery | undefined, RestError>([QueryKeys.DELIVERIES, deliveryID], async () => {
    const result = await api.deliveriesController.deliveriesGet(deliveryID);
    return new Delivery(result);
  });

  // subscribe to timeline update events
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    query.refetch();
  }, [query]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      return event.data.deliveryID === deliveryID;
    },
    [deliveryID]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [deliveryID],
      workflowIDs: [],
      projectIDs: []
    }),
    [deliveryID]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  return query;
};

export const useQueryGetDeliveryTimeline = (deliveryID: string) => {
  const api = useApi();

  const query = useQuery<TimelineDto | undefined, RestError>(
    [QueryKeys.DELIVERIES, deliveryID, QueryKeys.TIMELINE],
    async () => {
      return await api.deliveriesController.deliveriesTimeline(deliveryID);
    }
  );

  // subscribe to timeline update events
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    query.refetch();
  }, [query]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      return event.data.deliveryID === deliveryID;
    },
    [deliveryID]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [deliveryID],
      workflowIDs: [],
      projectIDs: []
    }),
    [deliveryID]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  return query;
};

export function useMutationResendDeliveryNotification(deliveryID: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    async (request: ResendNotificationDto) => {
      await api.deliveriesActionsController.manualDeliveryNotification(deliveryID, request);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.DELIVERIES, deliveryID, QueryKeys.TIMELINE]);
      }
    }
  );
}

export const useMutationMarkDeliveryCancel = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { deliveryID: string }>(
    async ({ deliveryID }) => {
      await api.deliveriesActionsController.markCancelled(deliveryID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERIES);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationForceUpdateDelivery = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { deliveryID: string } & ForceUpdateDeliveryDto>(
    async ({ deliveryID, state, note, isPrivate }) => {
      await api.deliveriesActionsController.forceUpdate(deliveryID, { state, note, isPrivate });
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERIES);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
        queryClient.invalidateQueries(QueryKeys.NOTES);
      }
    }
  );
};

export const useMutationMarkDeliveryCollected = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { deliveryID: string }>(
    async ({ deliveryID }) => {
      await api.deliveriesActionsController.markCollected(deliveryID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERIES);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationMarkDeliveryRetrieved = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { deliveryID: string }>(
    async ({ deliveryID }) => {
      await api.deliveriesActionsController.markRetrieved(deliveryID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERIES);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationAddNoteToDelivery = (deliveryId: string, content: CreateNoteDto) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      await api.deliveriesNotesController.create(deliveryId, content);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERIES);
        queryClient.invalidateQueries(QueryKeys.ADD_NOTE);
      }
    }
  );
};

export const useMutationSwitchSpace = (deliveryID: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (deliveryContainerID: string) => {
      await api.deliveriesActionsController.switchSpaceByDeliveryId(deliveryID, {
        channel: KnownReallocateDeliveryDtoChannel.Cloud,
        deliveryContainerID
      });
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.DELIVERIES, deliveryID, QueryKeys.TIMELINE]);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export const useMutationArchiveDelivery = (deliveryID: string) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError>(
    async () => {
      await api.deliveriesController.deliveriesDelete(deliveryID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.DELIVERIES, deliveryID, QueryKeys.TIMELINE]);
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
};

export function useQueryCanIOpenDeliveryLocker(deliveryID: string) {
  const api = useApi();

  return useQuery<CanIOpenDeliveryContainerDto, RestError>(
    [QueryKeys.DELIVERIES, deliveryID, QueryKeys.CAN_I_OPEN_LOCKER],
    async () => {
      const result = await api.deliveriesActionsController.canIOpenLockerByDeliveryId(deliveryID);
      return result;
    }
  );
}
